import { Card, Divider, Grid, Typography, Box } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import FormFileDrop from '../../../FormFields/FormFileDrop';
import FormTextFieldWithAddMore from '../../../FormFields/FormTextFieldWithAddMore';
import FormToggleField from '../../../FormFields/FormToggleField';
import FormTitle from '../../../FormFields/FormTitle';
import { formCardStyle } from '../../../TransitionStyles';
import { FILE_TYPES } from '../../../formFileTypesEnum';
import { ENVIRONMENTAL } from '../../../formQuestionsEnum';
import HousekeepingSection from './HousekeepingSection';
import FormDataDivider from '../../../FormFields/FormDataDivider';
import Equipment from './Equipment';
import EquipmentRentals from './EquipmentRentals';
import FormVendorField from '../../../FormFields/FormVendorField';

HousekeepingForm.propTypes = {
  permissions: PropTypes.object,
};

export default function HousekeepingForm({ permissions }) {
  const [focusedField, setFocusedField] = useState(null);

  const fieldIds = [
    ENVIRONMENTAL.HOUSEKEEPING_IS_OUTSOURCED.id,
    ENVIRONMENTAL.HOUSEKEEPING_OUTSOURCED_CATEGORY_ID.id,
    ENVIRONMENTAL.HOUSEKEEPING_OUTSOURCED_HOURS_PER_WEEK.id,
    ENVIRONMENTAL.HOUSEKEEPING_AVERAGE_MONTHLY_COST.id,
    ENVIRONMENTAL.HOUSEKEEPING_AND_LAUNDRY_OUTSOURCE_VENDOR.id,
    ENVIRONMENTAL.HOUSEKEEPING_SOAP.id,
    ENVIRONMENTAL.HOUSEKEEPING_SANITIZER.id,
    ENVIRONMENTAL.HOUSEKEEPING_LINERS.id,
    ENVIRONMENTAL.HOUSEKEEPING_PAPER_TOWELS.id,
    ENVIRONMENTAL.HOUSEKEEPING_TOILET_PAPER.id,
    ENVIRONMENTAL.HOUSEKEEPING_CHEMICALS.id,
    ENVIRONMENTAL.HOUSEKEEPING_STAFFING_SCHEDULE.id,
    ENVIRONMENTAL.HOUSEKEEPING_MAIN_SUPPLIER.id,
    ENVIRONMENTAL.HOUSEKEEPING_SUPPLIES.id,
    ENVIRONMENTAL.HOUSEKEEPING_EQUIPMENT_RENTALS.id,
    ENVIRONMENTAL.HOUSEKEEPING_EQUIPMENT.id,
  ];

  const showForm = fieldIds.some((id) => id in permissions);

  return (
    showForm && (
      <Card sx={formCardStyle}>
        <Typography sx={{ mb: 1 }} align="center" variant="body1" color="info.main">
          Environmental
        </Typography>
        <Divider sx={{ mb: 1, mt: 2, borderWidth: 1 }} />
        <Grid container spacing={1.5}>
          <HousekeepingSection permissions={permissions} />
          {ENVIRONMENTAL.HOUSEKEEPING_STAFFING_SCHEDULE.id in permissions && (
            <Grid item xs={12}>
              <FormTitle fieldAttributes={{ questionTitle: 'Housekeeping staffing schedule', fieldType: 'file' }} />
              <FormFileDrop
                fieldName={ENVIRONMENTAL.HOUSEKEEPING_STAFFING_SCHEDULE.fieldName}
                fieldAttributes={{
                  isEditable: permissions[ENVIRONMENTAL.HOUSEKEEPING_STAFFING_SCHEDULE.id],
                  fileTypeId: FILE_TYPES.HOUSEKEEPING_STAFFING_SCHEDULE,
                }}
              />
            </Grid>
          )}

          {ENVIRONMENTAL.HOUSEKEEPING_MAIN_SUPPLIER.id in permissions && (
            <Grid item xs={12}>
              <FormTitle
                fieldAttributes={{
                  questionTitle: 'Housekeeping supplies vendor',
                  fieldType: 'title',
                }}
              />
              <FormVendorField
                fieldName={ENVIRONMENTAL.HOUSEKEEPING_MAIN_SUPPLIER.fieldName}
                fieldAttributes={{
                  isEditable: permissions[ENVIRONMENTAL.HOUSEKEEPING_MAIN_SUPPLIER.id],
                }}
                invoiceFileTypeId={FILE_TYPES.HOUSEKEEPING_SUPPLIES_VENDOR_INVOICE}
                contractFileTypeId={FILE_TYPES.HOUSEKEEPING_SUPPLIES_VENDOR_CONTRACT}
              />
            </Grid>
          )}

          {ENVIRONMENTAL.HOUSEKEEPING_SUPPLIES.id in permissions && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <FormTitle
                fieldAttributes={{
                  questionTitle: 'List all housekeeping and laundry supplies that the facility orders',
                }}
              />
              <FormTextFieldWithAddMore
                fieldName={ENVIRONMENTAL.HOUSEKEEPING_SUPPLIES.fieldName}
                isEditable={permissions[ENVIRONMENTAL.HOUSEKEEPING_SUPPLIES.id]}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
              />
              <FormDataDivider />
            </Grid>
          )}

          {ENVIRONMENTAL.HOUSEKEEPING_EQUIPMENT_RENTALS.id in permissions && (
            <Grid container spacing={1.5} item xs={12}>
              <Grid item xs={12}>
                <FormTitle fieldAttributes={{ questionTitle: 'Equipment Rentals', fieldType: 'title' }} />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ whiteSpace: 'nowrap' }}>
                  <FormTitle fieldAttributes={{ questionTitle: 'Are there any rentals/leases?', fieldType: 'file' }} />
                </Box>

                <Box>
                  <FormToggleField
                    fieldName={`${ENVIRONMENTAL.HOUSEKEEPING_EQUIPMENT_RENTALS.fieldName}.hasRentals`}
                    fieldAttributes={{
                      isEditable: permissions[ENVIRONMENTAL.HOUSEKEEPING_EQUIPMENT_RENTALS.id],
                      label: '',
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <EquipmentRentals isEditable={permissions[ENVIRONMENTAL.HOUSEKEEPING_EQUIPMENT_RENTALS.id]} />
              </Grid>
              <FormDataDivider />
            </Grid>
          )}

          {ENVIRONMENTAL.HOUSEKEEPING_EQUIPMENT.id in permissions && (
            <Grid container spacing={1.5} item xs={12}>
              <Grid item xs={12}>
                <FormTitle fieldAttributes={{ questionTitle: 'Housekeeping equipment', fieldType: 'title' }} />
              </Grid>
              <Grid item xs={12}>
                <Equipment
                  isEditable={permissions[ENVIRONMENTAL.HOUSEKEEPING_EQUIPMENT.id]}
                  equipmentsFieldName={ENVIRONMENTAL.HOUSEKEEPING_EQUIPMENT.fieldName}
                />
              </Grid>
              <FormDataDivider />
            </Grid>
          )}
        </Grid>
      </Card>
    )
  );
}
